import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDown, Loader2, Send, Sparkles, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import AssistantTurn, { parseBlocks } from '@/components/writer/AssistantTurn';

import { ChatMarkdown } from '@/components/writer/ChatMarkdown';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { type WriterMessage, type WriterTurnDiff } from '@/hooks/useWriterThread';
import { useWriterThreadValue } from '@/components/writer/WriterThreadProvider';
import WriterFindSourcesPopover from '@/components/writer/WriterFindSourcesPopover';
import {
  WRITER_PREFILL_PROMPT_EVENT,
  type WriterPrefillPromptDetail,
} from '@/lib/writer/deepLink';
import { writerTurnStatus } from '@/lib/writer/turnStatus';

export { parseBlocks };

interface WriterChatPaneProps {
  projectId: string;
  onUserMessage?: (text: string) => void;
  /** Fires once per completed tool call. Parents use it to refresh
   *  side panels (e.g. the bibliography) that a tool may have mutated. */
  onToolResult?: (name: string) => void;
}

/**
 * Two-pane Writer's agent chat surface. Admin-only during beta — the
 * parent route is already gated by `<WriterGate>`, so this component
 * trusts that it only renders for admins. It streams SSE deltas from the
 * `writer-agent` edge function and renders artifact cards for fenced
 * `tex` blocks so accepted prose can flow into the compile pipeline
 * (wired in Phase 3).
 */
const WriterChatPane: React.FC<WriterChatPaneProps> = ({ projectId, onUserMessage, onToolResult }) => {
  const { threadId, messages, streaming, error, outOfCredits, loading, send } = useWriterThreadValue(projectId);
  const [draft, setDraft] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const seenToolsRef = useRef<Set<string>>(new Set());

  // The always-visible header indicator must never disagree with the
  // bubble's own status line, so both read the same helper.
  const streamingMessage = messages.find((m) => m.streaming && m.role === 'assistant');
  const turnStatus = writerTurnStatus({
    streaming,
    hasProse: Boolean(
      streamingMessage &&
        parseBlocks(streamingMessage.parts.map((p) => p.text).join('')).prose,
    ),
    activities: streamingMessage?.toolActivity,
    aiSlow: streamingMessage?.aiSlow,
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Touch keyboards have no Shift+Enter affordance, so Enter must insert a
  // newline there and Send stays an explicit tap.
  const [coarsePointer] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches,
  );

  // --- Scroll anchoring -------------------------------------------------
  // The message list is the only scroll container in the compact shell, so
  // it owns "stick to the newest message" semantics: jump instantly on first
  // paint, follow smoothly while the author is already at the bottom, and
  // never yank the view when they scrolled up to re-read.
  const nearBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);
  const [showJumpPill, setShowJumpPill] = useState(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = distance < 80;
    nearBottomRef.current = near;
    setShowJumpPill(!near);
  }, []);

  // Auto-grow the composer up to ~5 lines (max-h caps it, then it scrolls).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    if (nearBottomRef.current) scrollToBottom('auto');
  }, [draft, scrollToBottom]);


  useEffect(() => {
    if (loading) return;
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      nearBottomRef.current = true;
      setShowJumpPill(false);
      // Two frames: one for layout, one after markdown/code blocks settle.
      requestAnimationFrame(() => {
        scrollToBottom('auto');
        requestAnimationFrame(() => scrollToBottom('auto'));
      });
      return;
    }
    if (nearBottomRef.current) scrollToBottom('smooth');
  }, [messages, loading, scrollToBottom]);

  // Keyboard open/close (and browser chrome collapse) resizes the visual
  // viewport — re-anchor so the newest message and the composer stay visible.
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return;
    const onResize = () => {
      if (nearBottomRef.current) scrollToBottom('auto');
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [scrollToBottom]);

  // Tool-result bookkeeping is intentionally separate from scrolling.
  useEffect(() => {
    if (!onToolResult) return;
    for (const m of messages) {
      for (const [i, act] of (m.toolActivity ?? []).entries()) {
        const key = `${m.id ?? ''}:${i}`;
        if (act.status === 'done' && !seenToolsRef.current.has(key)) {
          seenToolsRef.current.add(key);
          onToolResult(act.name);
        }
      }
    }
  }, [messages, onToolResult]);


  // Layer 2 compile-failure CTAs deep-link into the composer via a
  // window CustomEvent. Prefill only — the author still hits Send.
  // See `src/lib/writer/deepLink.ts` and
  // `src/components/writer/WriterCompileStatusBanner.tsx`.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<WriterPrefillPromptDetail>).detail;
      if (!detail?.prompt) return;
      if (detail.projectId && detail.projectId !== projectId) return;
      setDraft(detail.prompt);
      // Defer focus until after React flushes the state update so the
      // caret lands at the end of the prefilled text.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    };
    window.addEventListener(WRITER_PREFILL_PROMPT_EVENT, handler);
    return () => window.removeEventListener(WRITER_PREFILL_PROMPT_EVENT, handler);
  }, [projectId]);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft('');
    onUserMessage?.(text);
    await send(text);
  };


  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Redundant on compact surfaces, where the Writer header + status
          strip already own the identity and compile state. */}
      <div className="hidden lg:flex border-b border-border/60 px-5 py-4 items-center justify-between">

        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Agentic chat
          </p>
          <p className="text-sm font-medium text-foreground mt-0.5">
            Writer · EU-hosted
          </p>
        </div>
        {streaming && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {turnStatus.label}
          </div>
        )}
      </div>




      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-5 space-y-5"
          data-testid="writer-chat-scroller"
        >
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <EmptyState />
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                message={m}
                projectId={projectId}
              />
            ))
          )}
          {outOfCredits ? (
            <div className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-primary mb-1">
                <Wallet className="h-4 w-4" />
                You are out of credits
              </div>
              <p className="text-muted-foreground mb-3">
                Your free credits are used up. Upgrade to a paid plan for more writing turns every month.
              </p>
              <Link to="/pricing">
                <Button size="sm">View plans</Button>
              </Link>
            </div>
          ) : error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
        </div>
        {showJumpPill && (
          <button
            type="button"
            onClick={() => {
              nearBottomRef.current = true;
              setShowJumpPill(false);
              scrollToBottom('smooth');
            }}
            data-testid="writer-chat-jump-latest"
            className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/95 px-3 py-1.5 text-xs font-medium text-foreground shadow-md backdrop-blur transition hover:bg-muted"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            Jump to latest
          </button>
        )}
      </div>

      <form
        onSubmit={onSubmit}
        className="shrink-0 border-t border-border/60 px-4 py-3 bg-background/95 backdrop-blur"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >

        <div className="flex items-end gap-2 rounded-xl border border-border/60 bg-muted/30 focus-within:border-primary/60 focus-within:bg-background transition px-3 py-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // On touch keyboards Enter must insert a newline — the visible
              // Send button is the only submit affordance there.
              if (coarsePointer) return;
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e as unknown as React.FormEvent);
              }
            }}
            rows={2}
            enterKeyHint={coarsePointer ? 'enter' : 'send'}
            placeholder="Ask Writer to draft, refine, cite, or compile…"
            className="flex-1 resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground/70 max-h-[8rem] overflow-y-auto"
            disabled={streaming}
            aria-label="Writer chat composer"
          />
          <Button
            type="submit"
            size="sm"
            disabled={!draft.trim() || streaming}
            className="shrink-0"
          >
            {streaming ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        {/* AI Act Art. 50 transparency + Section 312a BGB cost indication */}
        <p className="mt-2 px-1 text-[11px] leading-snug text-muted-foreground">
          Replies are generated by AI (Google Vertex AI, EU/EEA) and can be wrong — check facts and
          citations before you submit. Each turn draws credits from your balance based on actual model
          usage (about 1 credit for a typical writing turn); reviews of public arXiv papers stay free.
        </p>
        <div className="mt-2 flex items-center justify-between gap-2 px-1">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {coarsePointer ? 'Tap send' : 'Enter to send · Shift+Enter for newline'}
          </p>

          <WriterFindSourcesPopover
            disabled={streaming}
            onInsert={(text) =>
              setDraft((prev) => (prev ? `${prev.trimEnd()}\n${text}` : text))
            }
          />
        </div>
      </form>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center text-center py-16 gap-3">
    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
      <Sparkles className="h-5 w-5 text-primary" />
    </div>
    <div className="max-w-xs">
      <p className="text-sm font-medium text-foreground">Draft with Writer</p>
      <p className="text-xs text-muted-foreground mt-1">
        Ask for a section outline, a paragraph rewrite, a figure sketch, or
        a compile of the current draft. Memory is always on.
      </p>
    </div>
  </div>
);

const MessageBubble: React.FC<{
  message: WriterMessage;
  projectId: string;
}> = ({ message, projectId }) => {
  const isUser = message.role === 'user';

  if (!isUser) {
    return <AssistantTurn message={message} projectId={projectId} />;
  }

  const text = message.parts.map((p) => p.text).join('');
  return (
    <div className="flex flex-col gap-2 items-end group" role="log">
      <div
        className={cn(
          "max-w-[92%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-primary text-primary-foreground rounded-br-sm shadow-sm transition-all group-hover:shadow-md",
        )}
      >
        <ChatMarkdown text={text} />
      </div>
    </div>
  );
};

export default WriterChatPane;
